import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Card, CardBody, CardTitle, Spinner, Badge } from 'reactstrap';
import { withRouter } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import ImageMapper from '../ImageMapper/imageMapper';
import Axios from '../../apis/nodeServer';
import { fetchFloors } from '../../actions';
import './heatmapPreview.css';

class HeatmapPreview extends Component {
    state = {
        floorPlanImage: null,
        areas: [],
        loading: true,
        error: null,
        riskStats: {
            high: 0,
            medium: 0,
            low: 0
        }
    };

    componentDidMount() {
        console.log('📊 HeatmapPreview mounted');
        // Always use mock data for now (until we have real data)
        setTimeout(() => {
            this.generateMockPreview();
        }, 500);
    }

    componentDidUpdate(prevProps) {
        if (this.props.floorsList !== prevProps.floorsList && this.props.floorsList.length > 0) {
            clearTimeout(this.mockDataTimeout);
            this.loadPreviewData(this.props.floorsList[0].id);
        }
    }

    componentWillUnmount() {
        clearTimeout(this.mockDataTimeout);
    }

    generateMockPreview = () => {
        console.log('📊 Generating MOCK preview data...');
        
        // Create simple SVG floor plan (compact for dashboard)
        const mockFloorPlan = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(`
            <svg xmlns="http://www.w3.org/2000/svg" width="600" height="320" viewBox="0 0 600 320">
                <rect width="600" height="320" fill="#f8f9fa"/>
                <rect x="30" y="30" width="540" height="260" fill="#ffffff" stroke="#dee2e6" stroke-width="2"/>
                <text x="300" y="20" text-anchor="middle" font-size="13" font-weight="bold" fill="#495057">
                    Laboratory Floor Plan (Preview)
                </text>
                <text x="120" y="90" font-size="11" fill="#6c757d">Diluição</text>
                <text x="420" y="90" font-size="11" fill="#6c757d">Leitura</text>
                <text x="120" y="230" font-size="11" fill="#6c757d">Pesagem</text>
                <text x="420" y="230" font-size="11" fill="#6c757d">Identificação</text>
            </svg>
        `)}`;
        
        const mockAreas = [
            { 
                id: 1, 
                shape: 'circle', 
                coords: [150, 110, 12], 
                preFillColor: this.getRiskColor(0, 'High'),
                strokeColor: 'rgba(0,0,0,0.3)',
                lineWidth: 1,
                name: 'Lab A',
                text: 'Location 101',
                risk_score: 95,
                risk_level: 'High'
            },
            { 
                id: 2, 
                shape: 'circle', 
                coords: [300, 140, 12], 
                preFillColor: this.getRiskColor(0, 'High'),
                strokeColor: 'rgba(0,0,0,0.3)',
                lineWidth: 1,
                name: 'Lab B',
                text: 'Location 102',
                risk_score: 92,
                risk_level: 'High'
            },
            { 
                id: 3, 
                shape: 'circle', 
                coords: [450, 130, 12], 
                preFillColor: this.getRiskColor(0, 'High'),
                strokeColor: 'rgba(0,0,0,0.3)',
                lineWidth: 1,
                name: 'Storage A',
                text: 'Location 103',
                risk_score: 65,
                risk_level: 'High'
            },
            { 
                id: 4, 
                shape: 'circle', 
                coords: [180, 240, 12], 
                preFillColor: this.getRiskColor(0, 'High'),
                strokeColor: 'rgba(0,0,0,0.3)',
                lineWidth: 1,
                name: 'Processing',
                text: 'Location 104',
                risk_score: 58,
                risk_level: 'High'
            },
            { 
                id: 5, 
                shape: 'circle', 
                coords: [340, 260, 12], 
                preFillColor: this.getRiskColor(0, 'High'),
                strokeColor: 'rgba(0,0,0,0.3)',
                lineWidth: 1,
                name: 'Clean Room',
                text: 'Location 105',
                risk_score: 52,
                risk_level: 'High'
            },
            { 
                id: 6, 
                shape: 'circle', 
                coords: [110, 180, 11], 
                preFillColor: this.getRiskColor(0, 'Medium'),
                strokeColor: 'rgba(0,0,0,0.3)',
                lineWidth: 1,
                name: 'Warehouse',
                text: 'Location 106',
                risk_score: 35,
                risk_level: 'Medium'
            },
            { 
                id: 7, 
                shape: 'circle', 
                coords: [420, 240, 11], 
                preFillColor: this.getRiskColor(0, 'Medium'),
                strokeColor: 'rgba(0,0,0,0.3)',
                lineWidth: 1,
                name: 'Packaging',
                text: 'Location 107',
                risk_score: 32,
                risk_level: 'Medium'
            },
            { 
                id: 8, 
                shape: 'circle', 
                coords: [520, 250, 10], 
                preFillColor: this.getRiskColor(0, 'Low'),
                strokeColor: 'rgba(0,0,0,0.3)',
                lineWidth: 1,
                name: 'Office',
                text: 'Location 108',
                risk_score: 18,
                risk_level: 'Low'
            },
        ];

        const riskStats = {
            high: 5,
            medium: 2,
            low: 1
        };

        this.setState({
            floorPlanImage: mockFloorPlan,
            areas: mockAreas,
            riskStats,
            loading: false,
            error: null
        });
    }

    getRiskColor = (score, level) => {
        switch (level) {
            case 'Critical': return 'rgba(220, 20, 60, 0.8)';
            case 'High': return 'rgba(255, 99, 71, 0.8)';
            case 'Medium': return 'rgba(255, 215, 0, 0.8)';
            case 'Low': return 'rgba(50, 205, 50, 0.8)';
            default: return 'rgba(144, 238, 144, 0.7)';
        }
    };

    loadPreviewData = async (floorId) => {
        this.setState({ loading: true, error: null });
        try {
            const riskResponse = await Axios.instance.get('risk/heatmap/', {
                params: { floor_id: floorId, risk_type: 'total', period_days: 90 }
            });
            const floorResponse = await Axios.instance.get(`floors/${floorId}/`);

            const heatmapData = riskResponse.data.heatmap_data || [];
            
            // Calculate risk statistics (only High, Medium, Low - no Critical)
            const riskStats = { high: 0, medium: 0, low: 0 };
            heatmapData.forEach(location => {
                const level = location.risk_level.toLowerCase();
                // Combine critical into high
                if (level === 'critical' || level === 'high') {
                    riskStats.high++;
                } else if (riskStats.hasOwnProperty(level)) {
                    riskStats[level]++;
                }
            });

            const areas = heatmapData.map(location => ({
                id: location.location_id,
                shape: 'circle',
                coords: [...location.coordinates.split(',').map(Number), 12], // Smaller radius for preview
                preFillColor: this.getRiskColor(location.risk_score, location.risk_level),
                strokeColor: 'rgba(0,0,0,0.3)',
                lineWidth: 1,
            }));

            this.setState({
                areas,
                floorPlanImage: floorResponse.data.floor_plan,
                riskStats,
                loading: false
            });
        } catch (error) {
            console.error('Preview load error:', error);
            this.setState({ error: 'Could not load heatmap preview.', loading: false });
        }
    };

    handlePreviewClick = (e) => {
        // Evitar navegação quando clicar em elementos interativos
        if (e.target.tagName === 'BUTTON' || e.target.closest('button')) {
            return;
        }
        
        console.log('🔬 Risk Management Card clicked!', e.target);
        e.preventDefault();
        e.stopPropagation();
        
        // Tentar usar history primeiro, depois fallback para window.location
        if (this.props.history) {
            this.props.history.push('/risk-management');
        } else {
            window.location.href = '/risk-management';
        }
    }

    render() {
        const { floorPlanImage, areas, loading, error, riskStats } = this.state;
        const { t } = this.props;

        return (
            <Card className="heatmap-preview-card" onClick={this.handlePreviewClick}>
                <CardBody>
                    <div className="preview-header">
                        <CardTitle tag="h5" className="preview-title">
                            🔬 {t('riskManagement.title')}
                        </CardTitle>
                        <small className="text-muted">Click for details</small>
                    </div>
                    
                    {loading && (
                        <div className="preview-loading">
                            <Spinner size="sm" color="primary" />
                            <span className="ml-2">Loading...</span>
                        </div>
                    )}
                    
                    {error && (
                        <div className="preview-error">
                            <p className="text-danger mb-0">{error}</p>
                        </div>
                    )}
                    
                    {!loading && !error && floorPlanImage && (
                        <>
                            <div className="preview-stats">
                                <div className="stat-item high">
                                    <Badge color="warning" pill>{riskStats.high}</Badge>
                                    <span>High</span>
                                </div>
                                <div className="stat-item medium">
                                    <Badge color="info" pill>{riskStats.medium}</Badge>
                                    <span>Medium</span>
                                </div>
                                <div className="stat-item low">
                                    <Badge color="success" pill>{riskStats.low}</Badge>
                                    <span>Low</span>
                                </div>
                            </div>
                            
                            <div className="preview-map-container">
                                <ImageMapper
                                    src={floorPlanImage}
                                    map={{ name: 'preview-map', areas }}
                                    width={280}
                                    responsive
                                />
                            </div>
                            
                            <div className="preview-footer">
                                <small className="text-muted">📊 Last 90 days analysis</small>
                                <small className="preview-cta">👉 View full report</small>
                            </div>
                        </>
                    )}
                </CardBody>
            </Card>
        );
    }
}

const mapStateToProps = state => ({
    floorsList: state.floorsListReducer
});

export default withRouter(withTranslation()(connect(mapStateToProps, { fetchFloors })(HeatmapPreview)));
